import * as React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Grid,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DetailTabs from "../../projects/components/DetailTabs";
import { VariantSignificanceIcon } from "./VariantSignificanceIcon";
import PedigreeVisualization from "./PedigreeVisualization";

export default function VariantRow(props) {
  const data = props.row;
  const projectType = props.project_type?.toLowerCase();

  // Determine zygosity based on VAF threshold
  const getZygosity = (vaf) => {
    if (!vaf || vaf === "N/A") return null;
    const vafValue = parseFloat(vaf);
    if (isNaN(vafValue)) return null;
    return vafValue >= 0.8 ? "HOM" : "HET";
  };

  const vaf = data.sample_specific?.allele_frequency;
  const zygosity = getZygosity(vaf);

  // Format VAF for display
  const formatVAF = (vaf) => {
    if (vaf === null || vaf === undefined || vaf === "N/A") return "N/A";
    const vafValue = parseFloat(vaf);
    if (isNaN(vafValue)) return "N/A";
    return vafValue.toFixed(2);
  };

  // Format genotype for display (shorten indels)
  const formatGenotype = (genotype) => {
    if (!genotype || genotype === "N/A") return "N/A";
    if (genotype.length > 7) {
      return genotype.substring(0, 5) + "...";
    }
    return genotype;
  };

  // Parse inheritance patterns
  const parseInheritancePatterns = (inheritance) => {
    if (!inheritance || inheritance === "N/A") return ["N/A"];
    return inheritance
      .split(";")
      .map((pattern) => pattern.trim())
      .filter((pattern) => pattern.length > 0);
  };

  const inheritancePatterns = parseInheritancePatterns(data.gene_inheritance);

  // Parse ClinVar data
  const parseClinVarData = (clinvar) => {
    if (!clinvar || clinvar === "N/A") return ["N/A"];

    let entries = [];
    // If it's already an array, use it
    if (Array.isArray(clinvar)) {
      entries = clinvar;
    }
    // If it's a string with separators, split it
    else if (typeof clinvar === "string") {
      entries = clinvar
        .split(/[;,]/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    } else {
      entries = [String(clinvar)];
    }

    // Define severity order for ClinVar classifications
    const severityOrder = {
      pathogenic: 1,
      "likely pathogenic": 2,
      "uncertain significance": 3,
      "likely benign": 4,
      benign: 5,
      "conflicting interpretations of pathogenicity": 6,
      "drug response": 7,
      association: 8,
      "risk factor": 9,
      protective: 10,
      affects: 11,
      other: 12,
      "not provided": 13,
    };

    // Sort entries by severity
    return entries.sort((a, b) => {
      const aLower = a.toLowerCase();
      const bLower = b.toLowerCase();

      // Find matching severity keys
      const aSeverity = Object.keys(severityOrder).find((key) =>
        aLower.includes(key)
      );
      const bSeverity = Object.keys(severityOrder).find((key) =>
        bLower.includes(key)
      );

      const aOrder = aSeverity ? severityOrder[aSeverity] : 999;
      const bOrder = bSeverity ? severityOrder[bSeverity] : 999;

      return aOrder - bOrder;
    });
  };

  const clinvarEntries = parseClinVarData(
    data.clinical_significance || data.clinvar
  );

  return (
    <Box>
      <Accordion
        elevation={3}
        sx={{
          borderRadius: "5px",
          ml: "0px",
          mr: "5px",
          marginTop: "3px",
          "&:before": {
            display: "none",
          },
          "&.Mui-expanded": {
            marginX: "3px",
          },
        }}
      >
        <AccordionSummary
          expandIcon={<ArrowDropDownIcon />}
          onMouseDown={(event) => event.stopPropagation()}
          sx={{
            minHeight: "50px",
            padding: "0px",
            "& .MuiAccordionSummary-content": {
              margin: "0px",
              height: "50px",
              width: "100%",
            },
          }}
        >
          <Grid
            container
            sx={{
              display: "flex",
              height: "100%",
              flexWrap: "nowrap",
            }}
          >
            {/* Classification Icon column */}
            {projectType === "somatic" ? (
              <Grid
                item
                sx={{
                  height: "100%",
                  width: { xs: 40, sm: 50 },
                  flexShrink: 0,
                }}
              >
                <VariantSignificanceIcon
                  classification={data.cancervar_classification}
                  type="amp"
                />
              </Grid>
            ) : (
              <Grid
                item
                sx={{
                  height: "100%",
                  width: { xs: 40, sm: 50 },
                  flexShrink: 0,
                }}
              >
                <VariantSignificanceIcon
                  classification={data.intervar_classification}
                  type="acmg"
                />
              </Grid>
            )}

            {/* Gene Symbol and HGVS column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 150, sm: 180, md: 200 },
                flexShrink: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: "100%",
                  marginX: { xs: "5px", sm: "10px" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "left",
                  }}
                >
                  <Typography fontSize={{ xs: "0.8rem", sm: "0.9rem" }}>
                    {data.gene_symbol || "N/A"}
                  </Typography>
                  <Typography fontSize={{ xs: "0.65rem", sm: "0.7rem" }}>
                    {data.hgvsg || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Inheritance Pattern column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 150, sm: 180, md: 200 },
                flexShrink: 0,
                display: { xs: "none", sm: "block" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    maxHeight: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                    sx={{ flexShrink: 0 }}
                  >
                    Mode of Inheritance
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ py: 0.5, maxHeight: 300, overflow: "auto" }}>
                        {inheritancePatterns.map((pattern, index) => (
                          <Typography
                            key={index}
                            fontSize={"0.75rem"}
                            sx={{
                              mb:
                                index < inheritancePatterns.length - 1
                                  ? 0.5
                                  : 0,
                            }}
                          >
                            {pattern}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        maxWidth: { xs: "130px", sm: "160px", md: "180px" },
                        width: "100%",
                        maxHeight: "52px",
                        overflow: "hidden",
                        position: "relative",
                        "&::after":
                          inheritancePatterns.length > 1
                            ? {
                                content: '""',
                                position: "absolute",
                                marginTop: "0px",
                                left: 0,
                                right: 0,
                                height: "8px",
                                pointerEvents: "none",
                              }
                            : {},
                      }}
                    >
                      {inheritancePatterns.slice(0, 1).map((pattern, index) => (
                        <Typography
                          key={index}
                          fontSize={"0.75rem"}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            maxWidth: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          {pattern}
                        </Typography>
                      ))}
                      {inheritancePatterns.length > 1 && (
                        <Typography
                          fontSize={"0.65rem"}
                          sx={{
                            color: "#999",
                            fontStyle: "italic",
                            textAlign: "center",
                            marginTop: "-5px",
                          }}
                        >
                          +{inheritancePatterns.length - 1} more
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>

            {/* Consequence column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 120, sm: 140, md: 160 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                  >
                    Consequence
                  </Typography>
                  <Tooltip
                    title={data.coding_consequence || data.consequence || "N/A"}
                    arrow
                  >
                    <Typography
                      fontSize={"0.8rem"}
                      sx={{
                        maxWidth: { xs: "100px", sm: "120px", md: "140px" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {data.coding_consequence || data.consequence || "N/A"}
                    </Typography>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>

            {/* Pedigree column with visual tooltip */}
            {projectType === "germline_trio" && (
              <Grid
                item
                sx={{
                  height: "100%",
                  width: { xs: 140, sm: 150, md: 160 },
                  flexShrink: 0,
                  display: { xs: "none", md: "block" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography
                      fontSize={"0.7rem"}
                      color={"grey"}
                      textAlign="center"
                    >
                      Segregation
                    </Typography>
                    <Tooltip
                      title={
                        <PedigreeVisualization
                          pedigreeData={data.pedigree}
                          sampleData={{
                            genotype: data.sample_specific?.genotype,
                            read_depth: data.sample_specific?.read_depth,
                            allele_frequency:
                              data.sample_specific?.allele_frequency,
                            affected: true,
                          }}
                          sampleId={data.sample_id}
                        />
                      }
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "transparent",
                            maxWidth: "none",
                          },
                        },
                      }}
                    >
                      <Typography
                        fontSize={"0.8rem"}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            textDecoration: "underline",
                          },
                        }}
                      >
                        {/* Show sample genotype first, then pedigree genotypes */}
                        {formatGenotype(
                          data.sample_specific?.genotype || "N/A"
                        )}
                        {Object.entries(data.pedigree || {})
                          .filter(
                            ([key]) =>
                              key !== "family_id" && key !== "affected_members"
                          )
                          .map(([member, memberData]) => {
                            const genotype = memberData.genotype || "N/A";
                            return (
                              <React.Fragment key={member}>
                                <span
                                  style={{
                                    fontWeight: 400,
                                    color: "#47535eff",
                                    margin: "0 4px",
                                    fontSize: "0.8em",
                                  }}
                                >
                                  ◆
                                </span>
                                {formatGenotype(genotype)}
                              </React.Fragment>
                            );
                          })}
                      </Typography>
                    </Tooltip>
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Depth column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 70, sm: 80 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    Depth
                  </Typography>
                  <Typography fontSize={"0.8rem"}>
                    {data.sample_specific.read_depth || "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* VAF column with HOM/HET indicator */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 90, sm: 100 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <Typography fontSize={"0.7rem"} color={"grey"}>
                    VAF
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minHeight: "24px",
                    }}
                  >
                    <Typography
                      fontSize={"0.8rem"}
                      sx={{
                        minWidth: "32px",
                        textAlign: "right",
                      }}
                    >
                      {formatVAF(vaf)}
                    </Typography>
                    <Box
                      sx={{
                        width: "40px",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      {zygosity && (
                        <Chip
                          label={zygosity}
                          size="small"
                          sx={{
                            height: 18,
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            backgroundColor:
                              zygosity === "HOM" ? "#ff9800" : "#2196f3",
                            color: "white",
                            "& .MuiChip-label": {
                              px: 0.75,
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* gnomAD column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 100, sm: 120, md: 130 },
                flexShrink: 0,
                display: { xs: "none", lg: "block" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                  >
                    gnomad Genomes AF
                  </Typography>
                  <Typography fontSize={"0.8rem"}>
                    {data.gnomadg_af !== null && data.gnomadg_af !== undefined
                      ? parseFloat(data.gnomadg_af).toPrecision(2)
                      : "N/A"}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* ClinVar column */}
            <Grid
              item
              sx={{
                height: "100%",
                width: { xs: 140, sm: 160, md: 180 },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  py: 0.5,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    maxHeight: "100%",
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    fontSize={"0.7rem"}
                    color={"grey"}
                    textAlign="center"
                    sx={{ flexShrink: 0 }}
                  >
                    ClinVar
                  </Typography>
                  <Tooltip
                    title={
                      <Box sx={{ py: 0.5, maxHeight: 300, overflow: "auto" }}>
                        {clinvarEntries.map((entry, index) => (
                          <Typography
                            key={index}
                            fontSize={"0.75rem"}
                            sx={{
                              mb: index < clinvarEntries.length - 1 ? 0.5 : 0,
                            }}
                          >
                            {entry}
                          </Typography>
                        ))}
                      </Box>
                    }
                    arrow
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        maxWidth: { xs: "120px", sm: "140px", md: "160px" },
                        width: "100%",
                        maxHeight: "52px",
                        overflow: "hidden",
                        position: "relative",
                        "&::after":
                          clinvarEntries.length > 1
                            ? {
                                content: '""',
                                position: "absolute",
                                marginTop: "0px",
                                left: 0,
                                right: 0,
                                height: "8px",
                                pointerEvents: "none",
                              }
                            : {},
                      }}
                    >
                      {clinvarEntries.slice(0, 1).map((entry, index) => (
                        <Typography
                          key={index}
                          fontSize={"0.75rem"}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            maxWidth: "100%",
                            lineHeight: 1.5,
                          }}
                        >
                          {entry}
                        </Typography>
                      ))}
                      {clinvarEntries.length > 1 && (
                        <Typography
                          fontSize={"0.65rem"}
                          sx={{
                            color: "#999",
                            fontStyle: "italic",
                            textAlign: "center",
                            marginTop: "-5px",
                          }}
                        >
                          +{clinvarEntries.length - 1} more
                        </Typography>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </AccordionSummary>
        <AccordionDetails sx={{ overflow: "auto" }}>
          <DetailTabs variant={props.row} bam_files={props.bam_files} />
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}
